import { NavLink, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useTierGate } from "@/hooks/useTierGate";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { useMyNav } from "@/hooks/useMyNav";
import {
  LayoutDashboard,
  FileText,
  RefreshCcw,
  ArrowUpRight,
  LogOut,
  Loader2,
  HelpCircle,
  Palette,
  CalendarClock,
  Bell,
  Archive,
  BarChart3,
  Users,
  DollarSign,
  Building2,
  Lock,
  ShieldCheck,
  House,
  Briefcase,
  Layers,
  Inbox,
} from "lucide-react";
import type { PlanTier } from "@/lib/tiers";

type Who = "all" | "manager" | "executive" | "asked";
const menuItems: { title: string; url: string; icon: any; requiresTier?: PlanTier; who: Who }[] = [
  { title: "Home", url: "/home", icon: House, who: "all" },
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard, who: "manager" },
  { title: "Templates", url: "/templates", icon: FileText, who: "manager" },
  { title: "Cycles", url: "/cycles", icon: RefreshCcw, who: "manager" },
  { title: "Schedules", url: "/schedules", icon: CalendarClock, requiresTier: "team", who: "manager" },
  { title: "Archive", url: "/archive", icon: Archive, who: "manager" },
  { title: "Analytics", url: "/analytics", icon: BarChart3, requiresTier: "team", who: "manager" },
  { title: "Value Calculator", url: "/value", icon: DollarSign, who: "manager" },
  { title: "Contributors", url: "/admin/contributors", icon: Users, who: "manager" },
  { title: "Executives", url: "/executives", icon: ShieldCheck, who: "manager" },
  { title: "Team Members", url: "/settings/team", icon: Users, requiresTier: "organization", who: "manager" },
  { title: "Brand Kit", url: "/settings/brand", icon: Palette, requiresTier: "team", who: "manager" },
  { title: "Organization", url: "/settings/organization", icon: Building2, who: "manager" },
  { title: "Notifications", url: "/settings/notifications", icon: Bell, who: "manager" },
  { title: "Reports shared with me", url: "/executive", icon: Briefcase, who: "executive" },
  { title: "Roll-ups", url: "/executive/rollups", icon: Layers, who: "executive" },
  { title: "My contributions", url: "/my-achievements", icon: Inbox, who: "asked" },
  { title: "How It Works", url: "/how-it-works", icon: HelpCircle, who: "manager" },
];

const AppSidebar = () => {
  const { user, signOut, loading } = useAuth();
  const { hasFeature } = useSubscription();
  const { isPersonal, templateCount, cyclesThisMonth, limits } = useTierGate();
  const { nav } = useMyNav();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
  };

  const visible = menuItems.filter((i) =>
    i.who === "all" ||
    (i.who === "manager" && nav.is_manager) ||
    (i.who === "executive" && nav.is_executive) ||
    (i.who === "asked" && nav.was_asked)
  );

  return (
    <Sidebar className="border-r border-border">
      <SidebarHeader className="border-b border-border p-4">
        <div className="flex items-center justify-between gap-2">
          <Logo size="md" to={null} />
        </div>
      </SidebarHeader>

      <SidebarContent className="p-2">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visible.map((item) => {
                const isActive = location.pathname === item.url ||
                  (item.url !== "/dashboard" && item.url !== "/executive" && location.pathname.startsWith(item.url));
                const isLocked = !!item.requiresTier && !hasFeature(item.requiresTier);

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="flex-1">{item.title}</span>
                        {isLocked && <Lock className="w-3 h-3 opacity-60" />}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-4">
        <div className="flex flex-col gap-3">
          {nav.is_manager && isPersonal && (
            <Link
              to="/pricing"
              className="block rounded-lg border border-border bg-muted/40 hover:bg-muted hover:border-primary/30 transition-colors p-3 group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Personal plan
                </span>
                <ArrowUpRight className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <div className="space-y-1.5">
                <UsageRow
                  label="Templates"
                  used={templateCount}
                  limit={limits.maxTemplates}
                />
                <UsageRow
                  label="Cycles this mo."
                  used={cyclesThisMonth}
                  limit={limits.maxCyclesPerMonth}
                />
              </div>
              <p className="text-[11px] text-primary mt-2 font-medium group-hover:underline">
                Upgrade for unlimited →
              </p>
            </Link>
          )}
          <div className="text-sm text-muted-foreground truncate">
            {user?.email}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSignOut}
            disabled={loading}
            className="w-full justify-start gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            Sign Out
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

const UsageRow = ({ label, used, limit }: { label: string; used: number; limit: number }) => {
  const isUnlimited = limit === Infinity;
  const atCap = !isUnlimited && used >= limit;
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums font-medium ${atCap ? "text-primary" : "text-foreground"}`}>
        {used}{isUnlimited ? "" : `/${limit}`}
      </span>
    </div>
  );
};

export default AppSidebar;
